import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Toast, ToastViewport } from "./Toast";
import { Button } from "../atoms/Button";

/**
 * The imperative half of `Toast`.
 *
 * `Toast` is controlled — `open`, `onClose`, one message — which is right for
 * the component and wrong for the call site. A save handler that has just
 * awaited a mutation has nowhere to hang a piece of `open` state, and the four
 * or five it would need to hang (is it open, which tone, what text, which
 * timer) are the same four every time. So this owns the queue and hands the
 * caller one verb: `toast.success("Saved")`.
 *
 * ## What it refuses to do
 *
 * - **No copy.** Every string is the caller's, including `closeLabel` — the
 *   package rule. A library that bakes in "Close" cannot be translated.
 * - **No stack without a bound.** Only the newest `max` are mounted. A failing
 *   retry loop otherwise papers the corner over the content it is reporting on.
 * - **No second action.** `Toast` takes one control and so does this. Two
 *   choices in a corner that disappears on a timer is a dialog wearing the
 *   wrong clothes.
 * - **No promise/async sugar.** A toast that narrates a request's lifecycle
 *   ("Saving…" → "Saved") is a progress indicator, and it belongs next to the
 *   control the person pressed, not in the far corner of the screen.
 *
 * ## Durations, and why a failure has none
 *
 * A success is a receipt: it confirms something the person already believes
 * happened, so it can leave on its own. A failure is news, and it usually
 * carries the only copy of a reason ("card declined", "name already taken").
 * Dismissing that on a timer loses information the person has not read yet, so
 * `error` persists until closed. Passing `durationMs` overrides that for the
 * cases where the failure really is disposable.
 */
export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ToastOptions = {
  /**
   * Force an auto-dismiss duration, in milliseconds. Errors persist by
   * default; pass this to make a specific one auto-dismiss too. `null` pins
   * any toast open until it is closed.
   */
  durationMs?: number | null;
  /** One inline control — "Undo", "Retry", "View". Fires, then dismisses. */
  action?: ToastAction;
};

type ToastKind = "success" | "error" | "info" | "warning";

type Enqueue = (
  title: string,
  description?: string,
  options?: ToastOptions,
) => void;

export type ToastApi = {
  success: Enqueue;
  error: Enqueue;
  info: Enqueue;
  warning: Enqueue;
};

type ToastItem = {
  id: string;
  kind: ToastKind;
  title: string;
  description?: string;
  durationMs: number | null;
  action?: ToastAction;
};

const ToastContext = createContext<ToastApi | null>(null);

/** `error` is absent on purpose — see the note on durations above. */
const DURATION_MS: Record<Exclude<ToastKind, "error">, number> = {
  success: 3000,
  info: 4000,
  warning: 5000,
};

/** A success carrying an action stays longer: the action is the point of it. */
const SUCCESS_WITH_ACTION_MS = 5000;

/** `error` is the odd one out; the rest map straight through. */
const TONE = {
  success: "success",
  error: "danger",
  info: "info",
  warning: "warning",
} as const;

function resolveDuration(
  kind: ToastKind,
  options: ToastOptions | undefined,
): number | null {
  if (options && "durationMs" in options) return options.durationMs ?? null;
  if (kind === "error") return null;
  if (kind === "success" && options?.action) return SUCCESS_WITH_ACTION_MS;
  return DURATION_MS[kind];
}

export type ToastProviderProps = {
  children: ReactNode;
  /** Accessible name for every toast's close control. */
  closeLabel: string;
  /**
   * How many toasts are mounted at once, newest first. Older ones are dropped
   * rather than queued: a message about a state that has since changed twice
   * is not worth showing late.
   */
  max?: number;
  className?: string;
};

export function ToastProvider({
  children,
  closeLabel,
  max = 3,
  className,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // `useState`'s updater runs twice under StrictMode, so an id generated
  // inside it would differ between the two runs and remount the toast.
  const nextId = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const enqueue =
      (kind: ToastKind): Enqueue =>
      (title, description, options) => {
        const id = `toast_${(nextId.current += 1)}`;
        const item: ToastItem = {
          id,
          kind,
          title,
          description,
          durationMs: resolveDuration(kind, options),
          action: options?.action,
        };
        setToasts((current) => [...current, item].slice(-max));
      };

    return {
      success: enqueue("success"),
      error: enqueue("error"),
      info: enqueue("info"),
      warning: enqueue("warning"),
    };
  }, [max]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 ? (
        <ToastViewport className={className}>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              open
              tone={TONE[toast.kind]}
              duration={toast.durationMs ?? undefined}
              closeLabel={closeLabel}
              onClose={() => dismiss(toast.id)}
              action={
                toast.action ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      toast.action?.onClick();
                      dismiss(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </Button>
                ) : undefined
              }
            >
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold">{toast.title}</span>
                {toast.description ? (
                  <span className="text-text3">{toast.description}</span>
                ) : null}
              </span>
            </Toast>
          ))}
        </ToastViewport>
      ) : null}
    </ToastContext.Provider>
  );
}

/**
 * Throws outside a provider rather than returning a no-op API. A toast that
 * silently does nothing is indistinguishable from a mutation that silently did
 * nothing, and the call site cannot tell which it is looking at.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error("useToast must be used inside a <ToastProvider>");
  }
  return api;
}
